import {
  MetastructureField,
  MetastructureFieldType,
  ResourceType
} from '__types__';
import { z } from 'zod';

const metastructureField$ = z
  .object({
    active: z.boolean().optional().default(true),
    locked: z.boolean().optional().default(false),
    default: z.string().optional().default(''),
    required: z.boolean().optional().default(false),
    type: z.enum([
      MetastructureFieldType.Core,
      MetastructureFieldType.Custom,
      MetastructureFieldType.CustomGroup
    ]),
    name: z.string().optional(),
    custom_field_definition_id: z.number().optional(),
    custom_fieldgroup_definition_id: z.number().optional()
  })
  .passthrough();

const metastructure$ = z
  .object({
    archived: z.boolean().nullable().optional(),
    user_id: z.union([z.string(), z.number()]).nullable().optional(),
    fields: z.array(metastructureField$).optional().default([]),
    field_order: z.array(z.string()).optional()
  })
  .passthrough();

function metastructureFieldFromHttp(
  field: z.infer<typeof metastructureField$>
): MetastructureField | null {
  const base = {
    active: field.active,
    locked: field.locked,
    default: field.default,
    required: field.required
  };
  if (field.type === MetastructureFieldType.Core) {
    return field.name != null
      ? { ...base, type: field.type, name: field.name }
      : null;
  }
  if (field.type === MetastructureFieldType.Custom) {
    return field.custom_field_definition_id != null
      ? {
          ...base,
          type: field.type,
          custom_field_definition_id: field.custom_field_definition_id
        }
      : null;
  }
  return field.custom_fieldgroup_definition_id != null
    ? {
        ...base,
        type: field.type,
        custom_fieldgroup_definition_id: field.custom_fieldgroup_definition_id
      }
    : null;
}

export const resourceType$ = z
  .object({
    id: z.number(),
    name: z.string().nullable().optional(),
    icon: z.string().nullable().optional(),
    model: z
      .enum([
        'INDIVIDUAL',
        'ORGANIZATION',
        'LOCATION',
        'ITEM',
        'TALENT',
        'AGENT'
      ])
      .nullable()
      .optional(),
    internal: z.boolean().nullable().optional(),
    archived: z.boolean().nullable().optional(),
    order: z.number().nullable().optional(),
    metastructure: metastructure$.nullable().optional(),
    restricted_groups: z.array(z.number()).optional(),
    user_id: z.union([z.string(), z.number()]).nullable().optional(),
    created_at: z.string().nullable().optional(),
    updated_at: z.string().nullable().optional(),
    created_mechanism: z.string().nullable().optional()
  })
  .passthrough();

export const resourceTypeListResponse$ = z.union([
  z.array(resourceType$),
  z.object({
    results: z.array(resourceType$),
    paging: z.any().optional()
  })
]);

export function resourceTypeFromHttp(
  http: z.infer<typeof resourceType$>
): ResourceType & { restricted_groups?: number[] } {
  return {
    id: http.id,
    name: http.name ?? undefined,
    icon: http.icon ?? undefined,
    model: http.model ?? undefined,
    internal: http.internal ?? undefined,
    archived: http.archived ?? false,
    order: http.order ?? null,
    metastructure: {
      archived: http.metastructure?.archived ?? false,
      user_id:
        http.metastructure?.user_id != null
          ? String(http.metastructure.user_id)
          : null,
      fields: (http.metastructure?.fields ?? [])
        .map(metastructureFieldFromHttp)
        .filter((field): field is MetastructureField => field != null),
      field_order: http.metastructure?.field_order
    },
    restricted_groups: http.restricted_groups,
    user_id: http.user_id != null ? String(http.user_id) : null,
    created_at:
      http.created_at != null ? new Date(http.created_at) : new Date(),
    updated_at: http.updated_at != null ? new Date(http.updated_at) : null,
    created_mechanism: http.created_mechanism ?? null
  };
}

export function parseResourceTypeList(response: unknown): ResourceType[] {
  const parsed = resourceTypeListResponse$.parse(response);
  const items = Array.isArray(parsed) ? parsed : parsed.results;
  return items.map(resourceTypeFromHttp);
}

export function parseResourceType(response: unknown): ResourceType {
  return resourceTypeFromHttp(resourceType$.parse(response));
}
